import Descriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';
import { DescriptorJblOptionText } from '@r6digital/rapidstor-types/types/Descriptor';

import { unitOptionsTransformer, UpgradeDescriptor } from '@/features/rapidstor/UnitOptions';
import { resolveOptionCopy } from '@/journey-based-listing/utils/optionCopy';
import {
    findMatchingDeal,
    getUnitTypeRate,
} from '@/journey-based-listing/components/UnitSelect/utils/dealMatching';

import type { DisplayedOptions } from './displayedOptions';

export const CUSTOMISE_BOOKING_VIEWED = 'customise_booking_viewed';
export const CUSTOMISE_BOOKING_UPSELL_SELECTED = 'customise_booking_upsell_selected';
export const CUSTOMISE_BOOKING_CONTINUE = 'customise_booking_continue';

/** The server drops `extraData` over 10KB (`core/tracking.ts`), so the list is bounded. */
export const MAX_TRACKED_UPSELLS = 10;

/**
 * Declared as a type alias rather than an interface so it keeps the implicit index
 * signature that `TrackEventData` requires.
 */
export type TrackedUnitSummary = {
    /** The SiteLink unit type id (`UnitTypeID`). */
    unitTypeSl: number;
    /** The RapidStor unit type id (`_id`), which the funnel comparisons key on. */
    unitTypeRs: string;
    unitTypeName: string;
    descriptorName: string;
    width: number;
    length: number;
    area: number;
    floor: number;
    /** The deal-applied rate the visitor sees. */
    price: number;
    /** `stdRate`, the true standard rate. Not `rates.rate`, which holds the first deal's rate. */
    standardPrice: number;
    dealName: string | null;
    /** The copy the row rendered, resolved through the same helper the card uses. */
    heading: string;
    subheading: string;
};

/** `DisplayedOptionsKind` minus `none`, which is reported as a null `upsellType`. */
export type UpsellKind = 'descriptor' | 'unit-type' | 'next-size-up';

export type CustomiseBookingViewedPayload = {
    upsellDisplayed: boolean;
    upsellType: UpsellKind | null;
    base: TrackedUnitSummary;
    upsells: TrackedUnitSummary[];
};

/**
 * The arrival anchor for one customise-booking visit, plus the selections made since.
 *
 * Lives here rather than in the store so the builders below stay the only place that knows
 * the shape, and the store depends on the tracking code rather than the reverse.
 */
export interface UpsellFunnelSnapshot {
    base: TrackedUnitSummary;
    /** Held apart from `base` so an upgrade can be classed as descriptor vs unit-type. */
    baseDescriptorId: string;
    upsellType: UpsellKind | null;
    /** RapidStor `_id`s, in render order. The same id as `TrackedUnitSummary.unitTypeRs`. */
    offeredUnitTypeIds: string[];
}

export interface UpsellFunnel extends UpsellFunnelSnapshot {
    /** Unit type ids the visitor has committed to, in order. */
    selections: string[];
}

export type UpsellSelectedPayload = {
    upsellType: UpsellKind | null;
    /** 1-based, counting distinct options taken this visit. */
    selectionIndex: number;
    /** Where the option sat in the rendered list, 1-based. Null if it was not offered. */
    optionPosition: number | null;
    /** How many options were on screen to choose from. */
    optionCount: number;
    base: TrackedUnitSummary;
    selected: TrackedUnitSummary;
};

export type CustomiseBookingContinuePayload = {
    upsellDisplayed: boolean;
    upsellType: UpsellKind | null;
    /** The reserved unit differs from the one the visitor arrived on. */
    upgraded: boolean;
    upgradeKind: 'descriptor' | 'unit-type' | 'none';
    /** Signed, reserved minus anchor. Zero on a lateral switch to another floor. */
    areaDelta: number;
    priceDelta: number;
    selectionCount: number;
    /** True from the desktop button row, false from the card's Pay Now. */
    viaMoveIn: boolean;
    isNewReservation: boolean;
    base: TrackedUnitSummary;
    reserved: TrackedUnitSummary;
};

export interface BuildContinuePayloadArgs {
    funnel: UpsellFunnel;
    reserved: TrackedUnitSummary;
    reservedDescriptorId: string;
    viaMoveIn: boolean;
    isNewReservation: boolean;
}

export interface BuildCustomiseBookingPayloadArgs {
    displayed: DisplayedOptions;
    /** The descriptor of the base selection. */
    descriptor: Descriptor;
    selectedUnitType: UnitType;
    dealId: string | null;
    usingDealSelectorVariant: boolean;
}

export interface SummariseUnitTypeArgs {
    unitType: UnitType;
    descriptorName: string;
    dealId: string | null;
    priceOverride?: number;
    /** The descriptor's `jblOptionText`, as the selected card passes it. */
    optionText?: DescriptorJblOptionText;
    /** Set on a descriptor-upgrade row, where the descriptor supplies the copy. */
    upgradeDescriptor?: UpgradeDescriptor;
}

export function summariseUnitType({
    unitType,
    descriptorName,
    dealId,
    priceOverride,
    optionText,
    upgradeDescriptor,
}: SummariseUnitTypeArgs): TrackedUnitSummary {
    const matchingDeal = findMatchingDeal(unitType, dealId ?? undefined);
    const copy = resolveOptionCopy({ unitType, optionText, upgradeDescriptor });

    return {
        unitTypeSl: unitType.unitTypeId,
        unitTypeRs: unitType._id,
        unitTypeName: unitType.typeName,
        descriptorName,
        width: unitType.width,
        length: unitType.length,
        area: unitType.area,
        floor: unitType.floor,
        price: priceOverride ?? getUnitTypeRate(unitType, dealId ?? undefined),
        standardPrice: unitType.stdRate,
        dealName: matchingDeal?.title ?? unitType.deal?.title ?? null,
        heading: copy.heading,
        subheading: copy.subheading,
    };
}

/**
 * Mirrors OptionSelector's `selectedUnitRate`, which is the price rendered on the
 * selected card. The two paths differ, so the base row cannot just use `getUnitTypeRate`.
 */
function basePrice(
    selectedUnitType: UnitType,
    dealId: string | null,
    usingDealSelectorVariant: boolean,
): number {
    if (usingDealSelectorVariant) {
        return getUnitTypeRate(selectedUnitType, dealId ?? undefined);
    }
    return dealId
        ? selectedUnitType.rates.discountedRate
        : selectedUnitType.rates.rate;
}

interface DisplayedOptionRow {
    unitType: UnitType;
    descriptorName: string;
    /** Set on a descriptor-upgrade row; drives the heading the card renders. */
    upgradeDescriptor?: UpgradeDescriptor;
}

/**
 * The unit types behind the option rows on screen, in render order and uncapped.
 *
 * Both the event's `upsells` rows and the funnel's offered ids come from here, so the two
 * can never name different units.
 */
export function displayedOptionUnitTypes(
    displayed: DisplayedOptions,
    baseDescriptorName: string,
): DisplayedOptionRow[] {
    switch (displayed.kind) {
        case 'descriptor':
            return displayed.descriptorOptions
                .map((option): DisplayedOptionRow | null => {
                    // The card renders the descriptor through its representative unit
                    // type (`selected-card/index.tsx`), which is where size and price live.
                    const representative = unitOptionsTransformer(option)[0];
                    return representative
                        ? {
                            unitType: representative,
                            descriptorName: option.name,
                            upgradeDescriptor: option,
                        }
                        : null;
                })
                .filter((row): row is DisplayedOptionRow => row !== null);

        case 'unit-type':
            return displayed.unitTypeOptions.map(
                (unitType) => ({ unitType, descriptorName: baseDescriptorName }),
            );

        case 'next-size-up':
            return displayed.nextSizeUp
                ? [{
                    unitType: displayed.nextSizeUp.unitType,
                    descriptorName: displayed.nextSizeUp.descriptor.name,
                }]
                : [];

        default:
            return [];
    }
}

/**
 * Uncapped on purpose: `upsells` is trimmed to keep the event under the server's size
 * limit, but a selection still has to be recognisable as one of the options offered.
 */
export function collectOfferedUnitTypeIds(
    displayed: DisplayedOptions,
    baseDescriptorName: string,
): string[] {
    return displayedOptionUnitTypes(displayed, baseDescriptorName)
        .map((row) => row.unitType._id);
}

function summariseUpsells(
    displayed: DisplayedOptions,
    descriptor: Descriptor,
    dealId: string | null,
): TrackedUnitSummary[] {
    // The option rows carry no `jblOptionText`; only the selected card does.
    return displayedOptionUnitTypes(displayed, descriptor.name).map(
        (row) => summariseUnitType({
            unitType: row.unitType,
            descriptorName: row.descriptorName,
            dealId,
            upgradeDescriptor: row.upgradeDescriptor,
        }),
    );
}

export function buildCustomiseBookingPayload({
    displayed,
    descriptor,
    selectedUnitType,
    dealId,
    usingDealSelectorVariant,
}: BuildCustomiseBookingPayloadArgs): CustomiseBookingViewedPayload {
    const upsells = summariseUpsells(displayed, descriptor, dealId)
        .slice(0, MAX_TRACKED_UPSELLS);

    return {
        upsellDisplayed: displayed.kind !== 'none',
        upsellType: displayed.kind === 'none' ? null : displayed.kind,
        base: summariseUnitType({
            unitType: selectedUnitType,
            descriptorName: descriptor.name,
            dealId,
            priceOverride: basePrice(selectedUnitType, dealId, usingDealSelectorVariant),
            optionText: descriptor.jblOptionText,
        }),
        upsells,
    };
}

/** Keeps float noise out of the payload; areas and rates are at most two decimals. */
function delta(to: number, from: number): number {
    return Math.round((to - from) * 100) / 100;
}

/**
 * `selectionIndex` counts how many options the visitor has taken; `optionPosition` says
 * which one they took. `offeredUnitTypeIds` is captured in render order and uncapped,
 * which is what makes the position meaningful.
 */
export function buildUpsellSelectedPayload(
    funnel: UpsellFunnel,
    selected: TrackedUnitSummary,
    selectionIndex: number,
): UpsellSelectedPayload {
    const position = funnel.offeredUnitTypeIds.indexOf(selected.unitTypeRs);

    return {
        upsellType: funnel.upsellType,
        selectionIndex,
        optionPosition: position === -1 ? null : position + 1,
        optionCount: funnel.offeredUnitTypeIds.length,
        base: funnel.base,
        selected,
    };
}

/**
 * Conversion is measured against the anchor, not against membership of
 * `offeredUnitTypeIds`. A visitor who upgrades twice reserves a unit that was never in the
 * arrival offer, and a membership test would call that no upgrade at all.
 */
export function buildContinuePayload({
    funnel,
    reserved,
    reservedDescriptorId,
    viaMoveIn,
    isNewReservation,
}: BuildContinuePayloadArgs): CustomiseBookingContinuePayload {
    const upgraded = reserved.unitTypeRs !== funnel.base.unitTypeRs;

    const upgradeKind = ((): CustomiseBookingContinuePayload['upgradeKind'] => {
        if (!upgraded) {
            return 'none';
        }
        return reservedDescriptorId !== funnel.baseDescriptorId ? 'descriptor' : 'unit-type';
    })();

    return {
        upsellDisplayed: funnel.upsellType !== null,
        upsellType: funnel.upsellType,
        upgraded,
        upgradeKind,
        areaDelta: delta(reserved.area, funnel.base.area),
        priceDelta: delta(reserved.price, funnel.base.price),
        selectionCount: funnel.selections.length,
        viaMoveIn,
        isNewReservation,
        base: funnel.base,
        reserved,
    };
}
